'use client';

import * as Sentry from '@sentry/react';
import {
  User as FirebaseUser,
  GoogleAuthProvider,
  browserLocalPersistence,
  onIdTokenChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import { ReactNode, createContext, useCallback, useEffect, useState } from 'react';

import { toast } from '@/components/ui/hooks/use-toast';
import { axiosClient } from '@/lib/api-client';

import { clearImpersonation } from '@/auth/impersonation';
import { useCsrf } from '@/lib/csrf-context';
import { type LoggedInUser } from '@airreading/common/data';

import { auth } from './firebase';
import { getEnvironmentLevel } from '@/lib/common-utils';

export interface AuthContext {
  /**
   * undefined: onIdTokenChanged hasn't been called
   * null: user is not signed in
   * User: user signed in
   *
   */
  firebaseUser: FirebaseUser | null | undefined;
  loggedInUser: LoggedInUser | undefined;
  actingUser: LoggedInUser | undefined;

  login: () => unknown;
  logout: () => unknown;
  loading: boolean;
}

export const AuthContext = createContext<AuthContext>({
  firebaseUser: undefined,
  loggedInUser: undefined,
  actingUser: undefined,
  login: () => {},
  logout: () => {},
  loading: true,
});

export interface AuthProviderProps {
  children: ReactNode;
}

const googleOauthProvider = new GoogleAuthProvider();
googleOauthProvider.setCustomParameters({
  prompt: 'select_account',
});

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null | undefined>(undefined);
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const { csrfToken, refreshCsrfToken, clearCsrfToken } = useCsrf();

  // localhost uses popup since redirect auth is not reliable without
  // a same-site authDomain (/__/auth/* proxy)
  const shouldUseRedirect = getEnvironmentLevel() !== 'local';

  const airLogin = useCallback(() => {
    return axiosClient.get<LoggedInUser>('/me/login');
  }, []);

  useEffect(() => {
    if (csrfToken) {
      axiosClient.defaults.headers['x-csrf-token'] = csrfToken;
    }
  }, [csrfToken]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUserResp) => {
      sessionStorage.removeItem('auth:redirectInProgress');

      if (firebaseUserResp) {
        setFirebaseUser(firebaseUserResp);

        await refreshCsrfToken();
        await airLogin()
          .then(async ({ data: user }) => {
            Sentry.setUser({ id: user.id });

            setLoggedInUser(user);
            setLoading(false);
          })
          .catch(() => {
            toast({
              description: `Failed to login or we could not find your account ${firebaseUserResp.email}. Please contact Air Reading administrator.`,
            });
            setLoading(false);
            logout();
          });
      } else {
        setLoading(false);
        setFirebaseUser(null);
        setLoggedInUser(undefined);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [airLogin, toast, refreshCsrfToken]);

  const login = async () => {
    setLoading(true);
    await auth.setPersistence(browserLocalPersistence);

    if (shouldUseRedirect) {
      sessionStorage.setItem('auth:redirectInProgress', '1');
    }

    const signInMethod = shouldUseRedirect ? signInWithRedirect : signInWithPopup;

    return signInMethod(auth, googleOauthProvider).catch((error) => {
      sessionStorage.removeItem('auth:redirectInProgress');
      console.log(`${error.code}: ${error.message}`);
    });
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint to revoke Firebase refresh tokens
      await axiosClient.post('/me/logout');
    } catch (error) {
      Sentry.captureException(error);
      // Continue with client logout even if backend fails
    }

    // Clear local state
    clearImpersonation({ reload: false });
    clearCsrfToken();

    // Sign out from Firebase client (client-side)
    return signOut(auth);
  };

  const authValue = {
    firebaseUser,
    loggedInUser,
    actingUser: loggedInUser?.impersonatingAs || loggedInUser,
    login,
    logout,
    loading,
  };

  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
};
